import { useState, useEffect } from 'react';
import {Tabs, TabsProps } from 'antd';
import Cookies from 'js-cookie';

import './Main.css';

// Import other components
import About from '../About/About';
import BottomBar from '../BottomBar/BottomBar';
import NavigationBar from '../NavigationBar/NavigationBar';
import SideBar from '../SideBar/SideBar';
import ShowProjects from '../Projects/Projects';
import ShowSkills from '../Skills/Skills';

/**
 * StartPage component - Main application container and state manager.
 * 
 * This is the root component that orchestrates the entire application layout and state.
 * It manages data fetching, filtering, navigation, and responsive behavior.
 * 
 * @component
 * @returns {JSX.Element} The main application layout
 */
const StartPage = () => {
    // ==================== STATE MANAGEMENT ====================
    
    /** @type {Array} List of all projects loaded from projects.json */
    const [projectsList, setProjectsList] = useState([]);
    
    /** @type {Array} List of all skills loaded from skills.json */
    const [skillsList, setSkillsList] = useState([]);
    
    /** @type {Array} Unique tags extracted from projects and skills for filtering */
    const [allTags, setAllTags] = useState([]);
    
    /** @type {Array} Currently selected element [id, type] for highlighting/scrolling */
    const [selectedElement, setSelectedElement] = useState([]);
    
    /** @type {Array} Tags selected by user for filtering content */
    const [selectedTags, setSelectedTags] = useState([]);
    
    /** @type {boolean} Controls visibility of the sidebar menu */
    const [showSideMenu, setShowSideMenu] = useState(true);
    
    /** @type {string} Active tab key ("1"=Projects, "2"=Skills, "3"=About) */
    const [activeTabKey, setActiveTabKey] = useState("1");
    
    /** @type {string} Selected sidebar section for layout calculations */
    const [selectedSideBar, setSelectedSideBar] = useState("Projects");
    
    /** @type {number} Current window width for responsive behavior */
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    
    /** @type {string} Current language preference from cookies */
    const [language, setLanguage] = useState(Cookies.get('language') || 'de');

    // ==================== EFFECTS AND DATA LOADING ====================


    /**
     * Window resize handler for responsive design.
     * Updates windowWidth state when browser window is resized.
     */
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    /**
     * Data fetching effect - loads projects and skills from JSON files.
     * Runs once on component mount to populate the application with content.
     */
    useEffect(() => {
        // Fetch projects data
        fetch(process.env.PUBLIC_URL + "/projects.json")
            .then((response) => response.json())
            .then((data) => setProjectsList(data));
            
        // Fetch skills data
        fetch(process.env.PUBLIC_URL + "/skills.json")
            .then((response) => response.json())
            .then((data) => setSkillsList(data));
    }, []);


    /**
     * Tab synchronization effect.
     * Automatically switches to the appropriate tab when an element is selected
     * from the sidebar.
     */
    useEffect(() => {
        if (selectedElement[1] === "Projects") {
            setActiveTabKey("1");
        }
        if (selectedElement[1] === "Skills") {
            setActiveTabKey("2");
        }
        // Add other conditions if needed
    }, [selectedElement]);


    /**
     * Tag extraction and processing effect.
     * Extracts all unique technology tags from both projects and skills,
     * creates a sorted array for the filtering system.
     */
    useEffect(() => {
        var uniqueTags = new Set();
        
        // Extract tags from projects
        for (let i = 0; i < projectsList.length; i++) {
            let projectDetails = projectsList[i]["technic"];
            for (let j = 0; j < projectDetails.length; j++) {
                var nameOfTag = projectDetails[j]["name"];
                uniqueTags.add(nameOfTag);
            }
        }
        
        // Extract tags from skills
        for (let i = 0; i < skillsList.length; i++) {
            let projectDetails = skillsList[i]["technic"];
            for (let j = 0; j < projectDetails.length; j++) {
                var nameOfTag = projectDetails[j]["name"];
                uniqueTags.add(nameOfTag);
            }
        }
        
        const sortedTags = Array.from(uniqueTags).sort();
        setAllTags(sortedTags);
        console.log("Skillslist");
        console.log(skillsList);
    }, [projectsList, skillsList]);


    /**
     * Language preference monitoring effect.
     */
    useEffect(() => {
        const handleCookieChange = () => {
            const newLanguage = Cookies.get('language') || 'de';
            setLanguage(newLanguage);
        };

        // Check for language changes every second
        const interval = setInterval(handleCookieChange, 1000);

        return () => clearInterval(interval); // Cleanup on unmount
    }, []);


    // ==================== TAB CONFIGURATION ====================

    /**
     * Tab items
     * Defines the three main sections of the application.
     * 
     * @type {TabsProps['items']} Array of tab configuration objects
     */
    const items: TabsProps['items'] = [
        {
            key: '1',
            label: <span style={{ color: '#3F85AE' }}>Projects</span>,
            children: <ShowProjects 
                projectsList={projectsList} 
                selectedTags={selectedTags} 
                selectedElement={selectedElement} 
            />,
        },
        {
            key: '2',
            label: <span style={{ color: '#A7A706' }}>Skills</span>,
            children: <ShowSkills 
                skillsList={skillsList} 
                selectedTags={selectedTags} 
                selectedElement={selectedElement} 
            />,
        },
        {
            key: '3',
            label: <span style={{ color: '#5C9130' }}>About me</span>,
            children: <About />,
        },
    ];


    /**
     * Tab change handler.
     * Updates the active tab key when user clicks on a different tab.
     * 
     * @param {string} key - The key of the selected tab
     */
    const onChange = (key: string) => {
        console.log(key);
        setActiveTabKey(key);
    };

    // ==================== LOGIN HANDLING ====================

    /** @type {boolean} Controls authentication state of the user */
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    
    /** @type {string} Password input by the user for authentication */
    const [password, setPassword] = useState('');
    
    /** @type {string} Error message for failed login attempts */
    const [loginError, setLoginError] = useState('');


    /**
     * Login handler function.
     * Authenticates the user by checking the provided password against the stored password.
     */
    const handleLogin = () => {
        const correctPassword = process.env.REACT_APP_LOGIN_PASSWORD;
        
        if (password === correctPassword) {
            setIsAuthenticated(true);
            localStorage.setItem('authToken', 'authenticated');
            setLoginError('');
        } else {
            setLoginError('Wrong password. Please try again.');
            setPassword('');
        }
    };


    // ==================== RENDER ====================

    return (
        <div style={{ 
            background: 'linear-gradient(to right,rgb(175, 82, 175),rgb(39, 39, 131))', 
            minHeight: '100vh', 
            overflowX: 'hidden' 
        }}>
            {/* Navigation bar with search and language controls */}
            <NavigationBar 
                allTags={allTags} 
                selectedTags={selectedTags} 
                setSelectedTags={setSelectedTags} 
                showSideMenu={showSideMenu} 
                setShowSideMenu={setShowSideMenu} 
                windowWidth={windowWidth} 
            />
            
            {/*Sidebar*/}
            {showSideMenu && (
                <SideBar 
                    projectsList={projectsList} 
                    selectedTags={selectedTags} 
                    setSelectedElement={setSelectedElement} 
                    skillsList={skillsList} 
                    setSelectedSideBar={setSelectedSideBar} 
                    windowWidth={windowWidth} 
                />
            )}

            {/* Main content tabs*/}
            <Tabs
                className="custom-tabs"
                style={{ 
                    marginTop: '50px', 
                    marginLeft: selectedSideBar ? '250px' : '50px' 
                }}
                activeKey={activeTabKey}
                items={items}
                onChange={onChange}
            />
            
            {/* Fixed bottom status bar */}
            <BottomBar />
        </div>
    );
};

export default StartPage;